import {
  AccountFilters,
  MovementFilters,
  CashBoxBalance,
  MovementChartData,
} from '@/domain/accounts/account'
import { PaymentMethod } from '@/domain/payments/payments'
import { accountsApi } from '@/rest/modules/accounts-api'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useToast } from '../use-toast'
import { ApiResponse } from '@/domain'
import { MovementRequest } from '@/domain/accounts/expenses'
import { downloadCsv } from '@/components/features/shared/utils'

export const accountKeys = {
  all: ['accounts'] as const,
  lists: () => [...accountKeys.all, 'list'] as const,
  list: (filters: AccountFilters) => [...accountKeys.lists(), { filters }] as const,
  detail: (id: string) => [...accountKeys.all, 'detail', id] as const,
  movements: (accountId: string, filters: MovementFilters) => [...accountKeys.all, 'movements', accountId, { filters }] as const,
  movementCounts: (accountId: string, filters: MovementFilters) => [...accountKeys.all, 'movementCounts', accountId, { filters }] as const,
  movementChart: (accountId: string, filters: MovementFilters) => [...accountKeys.all, 'movementChart', accountId, { filters }] as const,
  cashboxMovements: (userId: string, filters?: { fromDate?: string; toDate?: string; paymentMethod?: PaymentMethod }) => [...accountKeys.all, 'userBalance', userId, { filters }] as const,
}

export const useAccounts = (filters?: AccountFilters, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: accountKeys.list(filters || {}),
    queryFn: () => accountsApi.getAccounts(filters),
    enabled: options?.enabled ?? true,
    staleTime: 100
  })
}

export const useAccount = (id?: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: accountKeys.detail(id!),
    queryFn: () => accountsApi.getAccount(id!),
    enabled: options?.enabled ?? !!id,
    staleTime: 0,
  })
}

export const useMovements = ({ accountId, filters }: { accountId?: string, filters: MovementFilters }) => {
  return useQuery({
    queryKey: accountKeys.movements(accountId!, filters),
    queryFn: () => accountsApi.getMovements(accountId!, filters),
    enabled: !!accountId,
  })
}

export const useMovementCounts = ({ accountId, filters }: { accountId?: string, filters: MovementFilters }) => {
  return useQuery({
    queryKey: accountKeys.movementCounts(accountId!, filters),
    queryFn: () => accountsApi.getMovementCounts(accountId!, filters),
    enabled: !!accountId,
  })
}

export const useMovementChart = ({ accountId, filters }: { accountId?: string, filters: MovementFilters }) => {
  return useQuery<MovementChartData[]>({
    queryKey: accountKeys.movementChart(accountId!, filters),
    queryFn: () => accountsApi.getMovementChart(accountId!, filters),
    enabled: !!accountId,
  })
}

export const useCashBoxMovements = (userId: string, filters?: { fromDate?: string; toDate?: string; includeCollectors?: boolean; paymentMethod?: PaymentMethod }) => {
  return useQuery<CashBoxBalance>({
    queryKey: accountKeys.cashboxMovements(userId, filters),
    queryFn: () => accountsApi.getCashBoxMovements(userId, filters),
    enabled: !!userId,
    staleTime: 0,
  })
}

export const useExportMovements = () => {
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ accountId, accountName, filters }: { accountId: string; accountName: string; filters?: MovementFilters }) =>
      accountsApi.exportMovements(accountId, filters),
    onSuccess: (response, variables) => {
      downloadCsv(response, `movimientos_cuenta_${variables.accountName}_${new Date().toLocaleDateString('es-AR')}.csv`)
      toast({
        title: 'Éxito',
        description: 'Movimientos exportados correctamente',
        variant: 'success',
      })
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al exportar movimientos',
        variant: 'destructive',
      })
    },
  })
}


export const useAddMovement = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (variables: { accountId: string; data: MovementRequest }) =>
      accountsApi.addMovement(variables.accountId, variables.data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: accountKeys.detail(variables.accountId) });
      queryClient.invalidateQueries({ queryKey: accountKeys.all });
      toast({
        title: 'Éxito',
        description: 'Gasto registrado exitosamente',
        variant: 'default',
      });
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al registrar el gasto',
        variant: 'destructive',
      });
    },
  });
};

export const useDeleteMovement = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (variables: { accountId: string; movementId: string }) =>
      accountsApi.deleteMovement(variables.accountId, variables.movementId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: accountKeys.detail(variables.accountId) });
      queryClient.invalidateQueries({ queryKey: accountKeys.all });
      toast({
        title: 'Éxito',
        description: 'Movimiento eliminado exitosamente',
        variant: 'default',
      });
    },
    onError: (response: ApiResponse<any>) => {
      toast({
        title: 'Error',
        description: response.message || response.error || 'Error al eliminar el movimiento',
        variant: 'destructive',
      });
    },
  });
};
